import {
  CameraIntegration,
  epochTimeOfSelectedEvent,
  getContactSupportMessage,
} from './base';
import Bowser from 'bowser';
import dayjs from 'dayjs';
import { post } from '../../helpers/securityService';
import { i18n } from '@qsrsoft/myq-core';

const messages = {
  en: {
    solink_auth_failed: 'We were unable to authenticate your Solink account.',
    solink_auth_failed_verify_creds: 'Failed to authenticate your Solink account. Please verify the entered credentials.',
    solink_access_denied: 'Access to the cameras associated with this location has been denied. Please ensure that your Solink account has the necessary permissions to access this location.',
    solink_no_thumbnails: 'We were unable to find the camera thumbnails associated with your Solink account. {contactSupportMessage}',
    solink_video_not_found: 'We were unable to find the video associated with this event.  If this error is reoccurring, {contactSupportMessage}',
    solink_no_cameras: 'We were unable to find the cameras associated with your Solink account. {contactSupportMessage}',
    solink_camera_not_found: 'We were unable to find the video for this camera because this camera was not found to be associated with your Solink account. {contactSupportMessage}',
    solink_stream_dates_not_found: 'We were unable to find the correct start and/or end time of the selected video. {contactSupportMessage}',
    solink_camera_id_not_found: 'We were unable to identify the selected camera in order to display the associated video. {contactSupportMessage}',
    solink_video_not_available: 'Video is not available from the selected camera for the requested period. Video is available from {earliestAvailableTimestamp} to {latestAvailableTimestamp}',
  },
  es: {
    solink_auth_failed: 'No pudimos autenticar su cuenta de Solink.',
    solink_auth_failed_verify_creds: 'No se pudo autenticar su cuenta de Solink. Por favor, verifique las credenciales ingresadas.',
    solink_access_denied: 'Se ha denegado el acceso a las cámaras asociadas con esta ubicación. Asegúrese de que su cuenta de Solink tenga los permisos necesarios para acceder a esta ubicación.',
    solink_no_thumbnails: 'No pudimos encontrar las miniaturas de cámara asociadas con su cuenta de Solink. {contactSupportMessage}',
    solink_video_not_found: 'No pudimos encontrar el video asociado con este evento. Si este error persiste, {contactSupportMessage}',
    solink_no_cameras: 'No pudimos encontrar las cámaras asociadas con su cuenta de Solink. {contactSupportMessage}',
    solink_camera_not_found: 'No pudimos encontrar el video de esta cámara porque no se encontró que estuviera asociada con su cuenta de Solink. {contactSupportMessage}',
    solink_stream_dates_not_found: 'No pudimos encontrar la hora de inicio y/o finalización correcta del video seleccionado. {contactSupportMessage}',
    solink_camera_id_not_found: 'No pudimos identificar la cámara seleccionada para mostrar el video asociado. {contactSupportMessage}',
    solink_video_not_available: 'El video no está disponible desde la cámara seleccionada para el período solicitado. El video está disponible desde {earliestAvailableTimestamp} hasta {latestAvailableTimestamp}',
  },
  fr: {
    solink_auth_failed: "Nous n'avons pas pu authentifier votre compte Solink.",
    solink_auth_failed_verify_creds: "Échec de l'authentification de votre compte Solink. Veuillez vérifier les identifiants saisis.",
    solink_access_denied: "L'accès aux caméras associées à cet établissement a été refusé. Assurez-vous que votre compte Solink dispose des autorisations nécessaires pour accéder à cet établissement.",
    solink_no_thumbnails: "Nous n'avons pas pu trouver les vignettes de caméra associées à votre compte Solink. {contactSupportMessage}",
    solink_video_not_found: "Nous n'avons pas pu trouver la vidéo associée à cet événement. Si cette erreur persiste, {contactSupportMessage}",
    solink_no_cameras: "Nous n'avons pas pu trouver les caméras associées à votre compte Solink. {contactSupportMessage}",
    solink_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte Solink. {contactSupportMessage}",
    solink_stream_dates_not_found: "Nous n'avons pas pu trouver l'heure de début et/ou de fin correcte de la vidéo sélectionnée. {contactSupportMessage}",
    solink_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. {contactSupportMessage}",
    solink_video_not_available: "La vidéo n'est pas disponible pour la caméra sélectionnée pendant la période demandée. La vidéo est disponible du {earliestAvailableTimestamp} au {latestAvailableTimestamp}",
  },
  'fr-ca': {
    solink_auth_failed: "Nous n'avons pas pu authentifier votre compte Solink.",
    solink_auth_failed_verify_creds: "Échec de l'authentification de votre compte Solink. Veuillez vérifier les identifiants saisis.",
    solink_access_denied: "L'accès aux caméras associées à cet établissement a été refusé. Assurez-vous que votre compte Solink dispose des autorisations nécessaires pour accéder à cet établissement.",
    solink_no_thumbnails: "Nous n'avons pas pu trouver les vignettes de caméra associées à votre compte Solink. {contactSupportMessage}",
    solink_video_not_found: "Nous n'avons pas pu trouver la vidéo associée à cet événement. Si cette erreur persiste, {contactSupportMessage}",
    solink_no_cameras: "Nous n'avons pas pu trouver les caméras associées à votre compte Solink. {contactSupportMessage}",
    solink_camera_not_found: "Nous n'avons pas pu trouver la vidéo de cette caméra car elle n'a pas été trouvée associée à votre compte Solink. {contactSupportMessage}",
    solink_stream_dates_not_found: "Nous n'avons pas pu trouver l'heure de début et/ou de fin correcte de la vidéo sélectionnée. {contactSupportMessage}",
    solink_camera_id_not_found: "Nous n'avons pas pu identifier la caméra sélectionnée pour afficher la vidéo associée. {contactSupportMessage}",
    solink_video_not_available: "La vidéo n'est pas disponible pour la caméra sélectionnée pendant la période demandée. La vidéo est disponible du {earliestAvailableTimestamp} au {latestAvailableTimestamp}",
  },
};

Object.keys(messages).forEach((locale) => {
  i18n.global.mergeLocaleMessage(locale, messages[locale]);
});

export class Solink extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'solink';
    this.getBrowserAndOS();
  }

  checkForCredentials() {
    this.isAuthenticated =
      !!this.camera?.accessToken &&
      this.camera?.accessToken !== 'refresh_token_invalid' &&
      this.camera?.accessToken !== 'refresh_token_not_available' &&
      this.camera?.accessToken !== 'solink_location_access_denied';
  }

  getBrowserAndOS() {
    // const deviceInfo = Bowser.getParser(window.navigator.userAgent);
    const { browser, os } = Bowser.parse(window.navigator.userAgent);
    this.browserName = browser.name.toLowerCase();
    this.osName = os.name.toLowerCase();
  }

  async submitCredentials(username, password) {
    const { location } = this.camera;
    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/solink_submit_creds/${location}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(i18n.global.t('solink_auth_failed'));
    });

    if (response && response === 'Error fetching new video obj after authentication with solink') {
      throw Error(i18n.global.t('solink_auth_failed_verify_creds'));
    }
    if ( response && response.accessToken === 'solink_location_access_denied' ) {
      throw Error(i18n.global.t('solink_access_denied'));
    }

    this.camera.accessToken = response.accessToken;
    this.camera.locationDetails = response.locationDetails;
    this.isAuthenticated = true;
  }

  async fetchThumbnails() {
    const {
      locationDetails: { cameraList },
    } = this.camera;

    const thumbnails = cameraList.map((camera) => ({
      src: camera.thumbnailUrl,
      title: camera.name,
      id: camera.id,
    }));

    if (thumbnails.length === 0) {
      console.warn('No cameras found for this location.');
      throw Error(
        i18n.global.t('solink_no_thumbnails', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    return thumbnails;
  }

  async fetchUrl(url) {
    let firstTsFileEntry = '';

    const { accessToken } = this.camera;

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    // const response = await axios.get(url, { headers }).catch(err => {
    //   console.error(err);
    //   throw Error(`We were unable to find the video associated with this event. Please try again later. If this error is reoccurring, ${contactSupportMessage}`);
    // });
    const response = await fetch(url, { headers }).catch((err) => {
      console.error(err);
      throw Error(
        i18n.global.t('solink_video_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    });

    const data = await response.text();

    const dataLines = data.split('\n');

    // Find the first entry in the line array
    // that matches the .ts extension
    // break out of the loop once entry is found
    try {
      dataLines.forEach((line) => {
        if (/\.ts$/.test(line)) {
          firstTsFileEntry = line;
          // eslint-disable-next-line no-throw-literal
          throw 'Break';
        }
      });
    } catch (e) {
      if (e !== 'Break') throw e;
    }

    return url;
  }

  async fetchVideo(cameraId, seekTime) {
    // event.time from alerts is in YYYYMMDDHHmmss format but fetchVideo from other pages are already in YYYY-MM-DD HH:mm:ss,
    // so we need to check the format and convert if necessary to ensure YYYY-MM-DD HH:mm:ss format for the video player
    const formattedTime = /^\d{14}$/.test(this.event.time)
      ? dayjs(this.event.time, 'YYYYMMDDHHmmss').format('YYYY-MM-DD HH:mm:ss')
      : this.event.time;
    const nowPlaying = {
      offsetSeconds: 0,
      displayTime: formattedTime,
      seekButtons: true,
    };

    const {
      timezone,
      commonSettings,
      //since cameraList is destructured from locationDetails it will error out without hitting the catch block
      locationDetails: { cameraList, deviceUrl } = {},
    } = this.camera;
    // If video seek button pressed
    if (seekTime) {
      nowPlaying.offsetSeconds += seekTime;
      nowPlaying.displayTime = dayjs(
        nowPlaying.displayTime,
        'YYYY-MM-DD HH:mm:ss',
      )
        .add(seekTime, 'second')
        .format('YYYY-MM-DD HH:mm:ss');
      this.event.time = nowPlaying.displayTime;
    }

    if (!cameraList || !cameraList[0]) {
      console.error('Missing cameraList:', { cameraList });
      throw Error(
        i18n.global.t('solink_no_cameras', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    if (cameraId)
      nowPlaying.camera = cameraList.find((cam) => cam.id === cameraId);
    else [nowPlaying.camera] = cameraList;

    if (Object.keys(nowPlaying.camera).length === 0) {
      console.error(
        'Could not find the camera associated with the provided camera id in the camera list',
        { cameraId, cameraList },
      );
      throw Error(
        i18n.global.t('solink_camera_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    const { streamEarliestSegmentDate, streamLatestSegmentDate } =
      nowPlaying.camera;

    if (!streamEarliestSegmentDate || !streamLatestSegmentDate) {
      console.error('Stream start and/or end date not found', {
        streamEarliestSegmentDate,
        streamLatestSegmentDate,
      });
      throw Error(
        i18n.global.t('solink_stream_dates_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    const videoDuration = commonSettings?.videoDurationSeconds || 30;
    const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
      .add(videoDuration, 'seconds')
      .format('HH:mm:ss');

    nowPlaying.event = this.event;
    nowPlaying.description = `${nowPlaying.displayTime} through ${videoEndTime} from the ${nowPlaying.camera.name} camera`;
    nowPlaying.cameraId = nowPlaying.camera.id;
    nowPlaying.accessToken = this.camera.accessToken;

    if (!nowPlaying.cameraId) {
      console.error(
        'Could not find the camera id from the selected camera needed to update the video player',
        nowPlaying.camera,
      );
      throw Error(
        i18n.global.t('solink_camera_id_not_found', { contactSupportMessage: getContactSupportMessage() }),
      );
    }

    const beginEpochMilliSeconds = epochTimeOfSelectedEvent({
      format: 'milli',
      time: nowPlaying.displayTime,
      timezone,
    });
    const endEpochMilliSeconds = dayjs(beginEpochMilliSeconds)
      .add(videoDuration, 'seconds')
      .valueOf();
    if (
      !(
        beginEpochMilliSeconds >= streamEarliestSegmentDate &&
        beginEpochMilliSeconds < streamLatestSegmentDate &&
        endEpochMilliSeconds > streamEarliestSegmentDate &&
        endEpochMilliSeconds <= streamLatestSegmentDate
      )
    ) {
      const earliestAvailableTimestamp = dayjs(
        streamEarliestSegmentDate,
      ).format('MM/DD/YYYY HH:mm:ss');
      const latestAvailableTimestamp = dayjs(streamLatestSegmentDate).format(
        'MM/DD/YYYY HH:mm:ss',
      );
      throw Error(
        i18n.global.t('solink_video_not_available', { earliestAvailableTimestamp, latestAvailableTimestamp }),
      );
    }

    const params = {
      begin: beginEpochMilliSeconds,
      end: endEpochMilliSeconds,
      stream: nowPlaying.camera.streamId,
    };
    const url = `${deviceUrl}/cameras/${
      nowPlaying.cameraId
    }/video.m3u8?${new URLSearchParams(params).toString()}`;

    const src = await this.fetchUrl(url);

    // Base video configuration
    nowPlaying.video = {
      sources: [{ src, type: 'application/x-mpegURL' }],
      ...this.commonVideoOptions,
    };

    // Force VHS usage on iOS/Safari instead of native HLS because Safari's native player does not use Video.js's custom
    // code including the part that adds the Authorization Bearer token to the request. 
    if (this.osName === 'ios' || this.browserName === 'safari') {
      // Aggressively force VHS by disabling native HLS support
      nowPlaying.video.html5 = {
        vhs: {
          overrideNative: true,
        },
        nativeVideoTracks: false,
        nativeAudioTracks: false,
        nativeTextTracks: false,
      };
      // Also set at root level to ensure it's picked up
      nowPlaying.video.techOrder = ['html5'];
      nowPlaying.video.enableSourceset = false;
    }

    return nowPlaying;
  }
}
